import * as React from 'react';
import { Ajax } from '@shared/Ajax';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { cs } from '@shared/utils/utils-classname';
import { Events } from '@shared/Events';
import './ModelsSurfaceHeightAtLocation.scss';

interface ISurfaceItem {
    id: number;
    name: string;
    height: number;
    area2D: number;
    area3D: number;
}

@observer
export class ModelsSurfaceHeightAtLocation extends React.Component<
    {
        lat?: number;
        lon?: number;
        modelIds?: number[];
    },
    {}
> {
    @observable isLoading = false;
    @observable items: ISurfaceItem[] = [];
    initialLoad = true;

    constructor(props: { lat?: number; lon?: number; modelIds?: number[] }) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.isLoading = true;
        this.fetch();
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(this.props.modelIds) !== JSON.stringify(prevProps.modelIds) || this.props.lat !== prevProps.lat || this.props.lon !== prevProps.lon) {
            this.fetch();
        }
    }

    fetch = _.debounce(() => {
        const { modelIds, lon, lat } = this.props;
        if (!modelIds || modelIds.length === 0 || !lat || !lon) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        Ajax({
            url: 'ajax_model_heights_at_location.json',
            data: {
                lat,
                lon,
                modelIdsCsv: modelIds.join(','),
            },
        })
            .done((resp: { models: ISurfaceItem[] }) => {
                if (resp.models) {
                    this.items = resp.models;
                }
            })
            .always(() => {
                this.isLoading = false;
                this.initialLoad = false;
            });
    }, 300);

    render() {
        if (!this.props.modelIds?.length) return <div>{__('js_map.no_models_loaded')}</div>;
        if (this.isLoading && (this.initialLoad || !this.items.length)) return <div>{__('js_logpoints.loading')}</div>;
        if (!this.isLoading && !this.items.length) return <div>{__('general.none')}</div>;

        const unit = kuura.getUnit().abbr;

        return (
            <div style={{ maxHeight: 400, overflow: 'auto' }} className={cs('models-surface-info', { 'fade-disable': this.isLoading })}>
                <table>
                    <tbody>
                        {this.items.map((item, i) => (
                            <tr key={i} onClick={() => Events.trigger('map-fit-model', { id: item.id })}>
                                <td className='no-break-text'>{item.name}</td>
                                {item.area2D >= 0 && (
                                    <td>
                                        <strong title={__('js_map.surface_elevation_at_point') + ': ' + String(item.height) + ' ' + String(unit)}>
                                            {item.height + ' ' + unit}
                                        </strong>
                                    </td>
                                )}
                                {item.area2D >= 0 && (
                                    <td>
                                        <strong
                                            title={
                                                __('js_map.surface_2D_area_projected', String(item.area2D), String(unit + '2')) +
                                                '\n' +
                                                __('js_map.surface_3D_area') +
                                                ': ' +
                                                String(item.area3D) +
                                                ' ' +
                                                String(unit + '2')
                                            }
                                        >
                                            {Math.round(item.area2D) + ' ' + unit}
                                            <sup>2</sup>
                                        </strong>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
}
